import React, { useState } from 'react';
import {
  Box,
  Grid,
  GridItem,
  VStack,
  Text,
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useToast,
  Flex,
  Tooltip,
  Icon,
  Checkbox,
  FormControl,
  FormLabel,
  Switch,
} from '@chakra-ui/react';
import { ThemeTemplate } from '../context/LyricsThemeContext';
import TemplatePreview from './TemplatePreview';
import TemplateEditor from './TemplateEditor';
import { useColorModeValue } from '@chakra-ui/react';
import { AddIcon, StarIcon, DeleteIcon, EditIcon, TemplateIcon } from './IconComponents';

interface TemplatesPanelProps {
  templates: ThemeTemplate[];
  currentTemplate: ThemeTemplate | null;
  saveTemplate: (template: Omit<ThemeTemplate, 'id' | 'isDefault'>, existingId?: string) => string;
  deleteTemplate: (id: string) => void;
  applyTemplate: (id: string) => void;
  setDefaultTemplate: (id: string) => void;
  accentColor: string;
}

const TemplatesPanel: React.FC<TemplatesPanelProps> = ({
  templates,
  currentTemplate,
  saveTemplate,
  deleteTemplate,
  applyTemplate,
  setDefaultTemplate,
  accentColor
}) => {
  // State for managing template editing
  const [selectedTemplate, setSelectedTemplate] = useState<ThemeTemplate | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<ThemeTemplate | null>(null);
  
  // UI hooks
  const { isOpen: isEditorOpen, onOpen: openEditor, onClose: closeEditor } = useDisclosure();
  const { isOpen: isDeleteModalOpen, onOpen: openDeleteModal, onClose: closeDeleteModal } = useDisclosure();
  const toast = useToast();
  
  // Colors
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'white');
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400');
  const scrollbarThumbColor = useColorModeValue('rgba(0,0,0,0.2)', 'rgba(255,255,255,0.2)');
  const scrollbarTrackColor = useColorModeValue('rgba(0,0,0,0.05)', 'rgba(255,255,255,0.05)');
  
  // Get the current default template
  const defaultTemplate = templates.find(t => t.isDefault);
  
  // Handle creating a new template
  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setIsEditing(false);
    openEditor();
  };
  
  // Handle editing a template
  const handleEditTemplate = (template: ThemeTemplate) => {
    setSelectedTemplate(template);
    setIsEditing(true);
    openEditor();
  };
  
  // Handle saving a template
  const handleSaveTemplate = (template: Omit<ThemeTemplate, 'id' | 'isDefault'>) => {
    if (isEditing && selectedTemplate) {
      // Pass the existing ID to preserve it when updating
      const updatedId = saveTemplate(template, selectedTemplate.id);
      
      toast({
        title: "Template updated",
        description: `Template "${template.name}" has been updated.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } else {
      // New template
      saveTemplate(template);
      toast({
        title: "Template created",
        description: `Template "${template.name}" has been created.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    }
    
    closeEditor();
  };
  
  // Prompt to delete a template
  const handlePromptDelete = (template: ThemeTemplate) => {
    setTemplateToDelete(template);
    openDeleteModal();
  };
  
  // Handle deleting a template
  const handleConfirmDelete = () => {
    if (!templateToDelete) return;
    
    deleteTemplate(templateToDelete.id);
    toast({
      title: "Template deleted",
      description: `Template "${templateToDelete.name}" has been deleted.`,
      status: "info",
      duration: 3000,
      isClosable: true,
    });
    
    setTemplateToDelete(null);
    closeDeleteModal();
  };
  
  // Handle setting a template as default
  const handleSetDefault = (template: ThemeTemplate) => {
    setDefaultTemplate(template.id);
    toast({
      title: "Default template set",
      description: `Template "${template.name}" is now the default and will be applied automatically.`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };
  
  return (
    <Box>
      <VStack spacing={4} align="stretch">
        <Flex justify="space-between" align="center">
          <Text fontSize="lg" fontWeight="bold" color={`${accentColor}.500`}>
            Manage Templates
          </Text>
          <Button 
            leftIcon={<AddIcon />} 
            colorScheme={accentColor} 
            size="sm"
            onClick={handleCreateTemplate}
          >
            Create New
          </Button>
        </Flex>
        
        {/* Info about default templates */}
        <Box 
          p={3} 
          bg={`${accentColor}.50`} 
          borderRadius="md" 
          borderLeft={`4px solid ${accentColor}.400`}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Text fontSize="sm" fontWeight="medium">
              {defaultTemplate 
                ? `Default template: "${defaultTemplate.name}"`
                : "No default template selected"
              }
            </Text>
            <Text fontSize="xs" color={mutedTextColor} mt={1}>
              The default template will be automatically applied when you open the app.
            </Text>
          </Box>
          {defaultTemplate && (
            <Tooltip label="Clear default">
              <Button 
                size="xs" 
                colorScheme={accentColor} 
                variant="outline"
                onClick={() => {
                  // Find a non-default template to set as "default" with priority 0
                  const nonDefaultTemplate = templates.find(t => !t.isDefault);
                  if (nonDefaultTemplate) {
                    setDefaultTemplate(nonDefaultTemplate.id);
                    toast({
                      title: "Default template cleared",
                      description: "No template will be automatically applied.",
                      status: "info",
                      duration: 3000,
                      isClosable: true,
                    });
                  }
                }}
              >
                Clear Default
              </Button>
            </Tooltip>
          )}
        </Box>
        
        {templates.length === 0 ? (
          <VStack 
            spacing={3} 
            p={8} 
            borderWidth="1px" 
            borderRadius="md" 
            borderColor={borderColor}
            borderStyle="dashed"
            justify="center"
            align="center"
          >
            <Icon as={TemplateIcon} boxSize={10} color={mutedTextColor} />
            <Text color={mutedTextColor}>No templates yet</Text>
            <Button
              size="sm"
              colorScheme={accentColor}
              variant="outline"
              leftIcon={<AddIcon />}
              onClick={handleCreateTemplate}
            >
              Create your first template
            </Button>
          </VStack>
        ) : (
          <Box 
            maxHeight="320px" 
            overflowY="auto"
            borderRadius="md"
            pr={2}
            css={{
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: scrollbarTrackColor,
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: scrollbarThumbColor,
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: accentColor === 'green' ? 'rgba(72, 187, 120, 0.6)' : 'rgba(237, 100, 166, 0.6)',
              },
              scrollbarWidth: 'thin',
              scrollbarColor: `${scrollbarThumbColor} ${scrollbarTrackColor}`,
            }}
          >
            <Grid templateColumns="repeat(auto-fill, minmax(240px, 1fr))" gap={4} p={1}>
              {templates.map(template => (
                <TemplatePreview 
                  key={template.id}
                  template={template}
                  isSelected={currentTemplate?.id === template.id}
                  isDefault={template.isDefault}
                  onClick={() => applyTemplate(template.id)}
                  onEdit={() => handleEditTemplate(template)}
                  onDelete={() => handlePromptDelete(template)}
                  onSetDefault={() => handleSetDefault(template)}
                  accentColor={accentColor}
                />
              ))}
            </Grid>
          </Box>
        )}
      </VStack>
      
      {/* Editor Modal */}
      <Modal isOpen={isEditorOpen} onClose={closeEditor} size="5xl">
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent bg={bgColor} borderWidth="1px" borderColor={borderColor}>
          <ModalHeader>{isEditing ? 'Edit Template' : 'Create Template'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <TemplateEditor
              template={selectedTemplate}
              onSave={handleSaveTemplate}
              onCancel={closeEditor}
              accentColor={accentColor}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={closeDeleteModal} isCentered size="sm">
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent bg={bgColor} borderWidth="1px" borderColor={borderColor}>
          <ModalHeader>Delete Template</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Text>
              Are you sure you want to delete the template "{templateToDelete?.name}"?
              This action cannot be undone.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="gray" mr={3} onClick={closeDeleteModal}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={handleConfirmDelete}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default TemplatesPanel; 